<!--
  TinoSalesFunnel.vue — SVG trapezoid funnel + breakdown sidebar.
  Ported from templates/dashboard/SalesFunnel.tsx.
  Props: tasks[], orders[] (optional; uses mock data if not provided).
-->
<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  orders: { type: Array, default: () => [] },
})

const { t } = useI18n()

/* ─── Funnel geometry ────────────────────────────────── */
const SVG_W   = 280
const STAGE_H = 56
const GAP     = 4
const WIDTHS  = [280, 200, 140, 84, 64]

const TOTAL_H = computed(() => 4 * STAGE_H + 3 * GAP)

function trapezoidPath(topW, botW, y, h, cx) {
  const tx = cx - topW / 2
  const bx = cx - botW / 2
  return `M ${tx} ${y} L ${tx + topW} ${y} L ${bx + botW} ${y + h} L ${bx} ${y + h} Z`
}

function conversionRate(from, to) {
  if (!from) return 0
  return Math.round((to / from) * 100)
}

/* ─── Stage data (derived or mock) ─────────────────── */
const stages = computed(() => {
  // Future: derive from tasks/orders when API is wired.
  // For now use static mock values matching the template.
  return [
    {
      label:         t('dashboard.tino.funnel.leads'),
      sublabel:      t('dashboard.tino.funnel.leadsLabel'),
      value:         150,
      color:         '#6366f1',
      gradientId:    'sf-g0',
      gradientStart: '#818cf8',
      gradientEnd:   '#6366f1',
    },
    {
      label:         t('dashboard.tino.funnel.quoted'),
      sublabel:      t('dashboard.tino.funnel.quotedLabel'),
      value:         45,
      color:         '#8b5cf6',
      gradientId:    'sf-g1',
      gradientStart: '#a78bfa',
      gradientEnd:   '#8b5cf6',
    },
    {
      label:         t('dashboard.tino.funnel.followUp'),
      sublabel:      t('dashboard.tino.funnel.followUpLabel'),
      value:         30,
      color:         '#f59e0b',
      gradientId:    'sf-g2',
      gradientStart: '#fbbf24',
      gradientEnd:   '#f59e0b',
    },
    {
      label:         t('dashboard.tino.funnel.won'),
      sublabel:      t('dashboard.tino.funnel.wonLabel'),
      value:         12,
      color:         '#22c55e',
      gradientId:    'sf-g3',
      gradientStart: '#4ade80',
      gradientEnd:   '#22c55e',
    },
  ]
})

const total = computed(() => stages.value[0]?.value ?? 1)
const finalRate = computed(() => conversionRate(total.value, stages.value.at(-1)?.value ?? 0))
const closingRatio = computed(() => Math.round(total.value / (stages.value.at(-1)?.value || 1)))

const cx = SVG_W / 2
</script>

<template>
  <VCard class="tino-funnel-card" flat>
    <!-- Header -->
    <div class="tino-funnel-header">
      <div>
        <div class="d-flex align-center gap-2 mb-0">
          <VIcon icon="mdi-filter-outline" size="18" color="primary" />
          <span class="text-sm font-weight-semibold">{{ t('dashboard.tino.funnel.title') }}</span>
        </div>
        <p class="text-xs text-medium-emphasis mb-0 mt-1">
          {{ t('dashboard.tino.funnel.subtitle', { total }) }}
        </p>
      </div>
      <VChip size="small" color="success" variant="tonal" class="font-weight-bold">
        {{ t('dashboard.tino.funnel.conversionRate', { rate: finalRate }) }}
      </VChip>
    </div>

    <VDivider />

    <!-- Body: SVG funnel + breakdown list -->
    <div class="tino-funnel-body">
      <!-- SVG Funnel -->
      <div class="tino-funnel-svg-wrap">
        <svg
          :width="SVG_W"
          :height="TOTAL_H"
          :viewBox="`0 0 ${SVG_W} ${TOTAL_H}`"
          overflow="visible"
        >
          <defs>
            <linearGradient
              v-for="s in stages"
              :key="s.gradientId"
              :id="s.gradientId"
              x1="0%" y1="0%" x2="0%" y2="100%"
            >
              <stop offset="0%" :stop-color="s.gradientStart" stop-opacity="1" />
              <stop offset="100%" :stop-color="s.gradientEnd" stop-opacity="1" />
            </linearGradient>
          </defs>

          <g v-for="(s, i) in stages" :key="s.label">
            <!-- Trapezoid -->
            <path
              :d="trapezoidPath(WIDTHS[i], WIDTHS[i + 1], i * (STAGE_H + GAP), STAGE_H, cx)"
              :fill="`url(#${s.gradientId})`"
            />

            <!-- Value label -->
            <text
              :x="cx"
              :y="i * (STAGE_H + GAP) + STAGE_H / 2 - 5"
              text-anchor="middle"
              dominant-baseline="middle"
              fill="white"
              font-size="15"
              font-weight="700"
              font-family="Inter, system-ui, sans-serif"
            >{{ s.value }}</text>

            <!-- Sublabel -->
            <text
              :x="cx"
              :y="i * (STAGE_H + GAP) + STAGE_H / 2 + 13"
              text-anchor="middle"
              dominant-baseline="middle"
              fill="rgba(255,255,255,0.75)"
              font-size="9"
              font-family="Inter, system-ui, sans-serif"
            >{{ s.sublabel }}</text>

            <!-- Conversion rate arrow between stages -->
            <text
              v-if="i < stages.length - 1"
              :x="cx + WIDTHS[i + 1] / 2 + 10"
              :y="i * (STAGE_H + GAP) + STAGE_H + GAP / 2"
              text-anchor="start"
              dominant-baseline="middle"
              fill="#9ca3af"
              font-size="9"
              font-family="Inter, system-ui, sans-serif"
            >↓ {{ conversionRate(s.value, stages[i + 1].value) }}%</text>
          </g>
        </svg>
      </div>

      <!-- Breakdown sidebar -->
      <div class="tino-funnel-breakdown">
        <div
          v-for="(s, i) in stages"
          :key="s.label"
          class="tino-funnel-stage"
        >
          <!-- Stage name + value -->
          <div class="d-flex align-center justify-space-between mb-1">
            <div class="d-flex align-center gap-2">
              <span
                class="tino-dot"
                :style="{ background: s.color }"
              />
              <span class="text-xs font-weight-semibold">{{ s.label }}</span>
            </div>
            <div class="d-flex align-center gap-2">
              <span
                v-if="i > 0"
                class="text-xs font-weight-medium"
                style="color: #ef4444"
              >−{{ stages[i - 1].value - s.value }}</span>
              <span class="text-xs font-weight-bold">{{ s.value }}</span>
            </div>
          </div>

          <!-- Progress bar -->
          <div class="tino-progress-track">
            <div
              class="tino-progress-fill"
              :style="{
                width: `${Math.round((s.value / total) * 100)}%`,
                background: s.color,
              }"
            />
          </div>

          <!-- Sub-row -->
          <div class="d-flex align-center justify-space-between mt-1">
            <span class="text-[10px] text-medium-emphasis">{{ s.sublabel }}</span>
            <span class="text-[10px] font-weight-medium text-medium-emphasis">
              {{ Math.round((s.value / total) * 100) }}% del total
            </span>
          </div>
        </div>

        <!-- Insight box -->
        <div class="tino-insight-box">
          <p class="text-xs text-primary-darken-1 leading-relaxed mb-0">
            <strong>{{ t('dashboard.tino.funnel.closingRate', { n: closingRatio }) }}</strong>
            {{ ' ' }}{{ t('dashboard.tino.funnel.improveTip') }}
          </p>
        </div>
      </div>
    </div>
  </VCard>
</template>

<style scoped lang="scss">
.tino-funnel-card {
  border: 1px solid rgba(var(--v-border-color), 0.1);
  border-radius: 16px !important;
  background: rgb(var(--v-theme-surface));
  overflow: hidden;
}

.tino-funnel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px;
}

.tino-funnel-body {
  display: flex;
  align-items: flex-start;
  gap: 28px;
  padding: 16px 20px 20px;
}

.tino-funnel-svg-wrap {
  flex-shrink: 0;
}

.tino-funnel-breakdown {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-block-start: 4px;
}

.tino-funnel-stage {
  // each stage row
}

.tino-dot {
  display: inline-block;
  inline-size: 10px;
  block-size: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.tino-progress-track {
  block-size: 6px;
  border-radius: 999px;
  background: rgba(var(--v-theme-on-surface), 0.07);
  overflow: hidden;
}

.tino-progress-fill {
  block-size: 100%;
  border-radius: 999px;
  transition: width 0.4s ease;
}

.tino-insight-box {
  margin-block-start: 4px;
  background: rgba(var(--v-theme-primary), 0.06);
  border: 1px solid rgba(var(--v-theme-primary), 0.15);
  border-radius: 12px;
  padding: 10px 14px;
}

@media (max-width: 640px) {
  .tino-funnel-body {
    flex-direction: column;
  }

  .tino-funnel-svg-wrap svg {
    inline-size: 100%;
  }
}
</style>
